import React from 'react';
import {
	areaMeta,
	areaVars,
	resolveAssignees,
	dueInfo,
	gateMeta,
	sourceMeta,
	childProgress,
	formatShortDate,
} from './taskModel';
import { isActionable, isNeedsInfo, changeCount } from './triageProposal';
import { Avatar } from './Avatar';

// Portal Phase C — small presentational primitives shared by row, card, detail.
// Every color comes from a CSS var (set via --row-accent / --chip-color) so the
// whole set recolors across Default / Comfort / Comfort-Dark.

// Inline style that points --row-accent at a given area's accent var.
export function accentStyle(area) {
	const v = areaVars(area).accent;
	return { '--row-accent': `var(${v})` };
}

export function StatusCircle({ status, size = 'md', onClick, title }) {
	const cls = `wt-status wt-status--${status}${size === 'sm' ? ' wt-status--sm' : ''}`;
	const inner = (
		<span className="wt-status__ring">
			{status === 'completed' && (
				<i className="fa-sharp fa-solid fa-check" />
			)}
		</span>
	);
	if (!onClick) return <span className={cls}>{inner}</span>;
	return (
		<button
			type="button"
			className={cls}
			title={title || 'Advance status'}
			aria-label={title || 'Advance status'}
			onClick={(e) => {
				e.stopPropagation();
				onClick(e);
			}}>
			{inner}
		</button>
	);
}

export function AreaTag({ area, dotOnly = false }) {
	const meta = areaMeta(area);
	if (dotOnly) return <span className="wt-tag__dot" title={meta.label} />;
	if (!area) return null;
	return (
		<span className="wt-tag">
			<span className="wt-tag__dot" />
			{meta.label}
		</span>
	);
}

// Quiet area (dot + label, muted) — the row/desktop-column treatment. The filled
// AreaTag pill is reserved for board cards + detail; in the list the area IS the
// 4px left bar, so the row shows the area only quietly (no repeated pill).
export function AreaQuiet({ area }) {
	if (!area) return null;
	const meta = areaMeta(area);
	return (
		<span className="wt-areaquiet" style={accentStyle(area)}>
			<span className="wt-areaquiet__dot" />
			{meta.label}
		</span>
	);
}

// `size` drives the photo/initial hybrid: <22px → initial (dense in-row / board
// footer where you scan), >=24px → real headshot (identify). Default 20 = the
// scanning size; detail/comments/filters pass 24+.
export function AssigneeChip({ task, initialOnly = false, size = 20 }) {
	const people = resolveAssignees(task);
	if (!people.length) return null;
	if (people.length === 1) {
		const p = people[0];
		return (
			<span className="wt-chip">
				<Avatar uid={p.uid} size={size} name={p.name} />
				{!initialOnly && <span className="wt-chip__name">{p.name}</span>}
			</span>
		);
	}
	// Multiple distinct people → a small avatar stack (still one chip per person).
	return (
		<span className="wt-chip wt-chip__stack">
			{people.map((p) => (
				<Avatar key={p.uid} uid={p.uid} size={size} name={p.name} />
			))}
		</span>
	);
}

export function DueLabel({ task, now }) {
	const info = dueInfo(task.deadline, now);
	if (!info) return null;
	return (
		<span className={`wt-due wt-due--${info.state}`}>
			<i className={`fa-sharp fa-solid ${info.icon}`} />
			{info.state === 'overdue' ? `${info.label}` : info.label}
		</span>
	);
}

export function GateBadge({ gate, forceLabel = false }) {
	const meta = gateMeta(gate);
	if (!meta) return null;
	return (
		<span className="wt-gate" title={meta.label}>
			<i className={`fa-sharp-duotone fa-solid ${meta.icon}`} />
			<span className={forceLabel ? undefined : 'wt-gate__label'}>
				{meta.label}
			</span>
		</span>
	);
}

export function SourceIcon({ source }) {
	const meta = sourceMeta(source);
	return (
		<i
			className={`fa-sharp fa-solid ${meta.icon} wt-source`}
			title={meta.label}
		/>
	);
}

// MailChip — "this card came from email", visible on EVERY surface at EVERY
// width (Anthony, 2026-09-05, on the list view: "add a mail icon … so that I
// can see and separate mail from other things"). The generic SourceIcon lives
// in a desktop-only column and paints at 0.7 opacity — the size-gated /
// quiet-affordance traps in CLAUDE.md — so mail gets its own chip in the
// title line. It also carries the close signal when the card has one: amber
// while the family is waiting on us, teal once someone replied. Renders
// nothing for a non-email card, so no other row changes.
export function MailChip({ task }) {
	if (!task || task.source !== 'email') return null;
	const r = task.mailReply;
	let state = 'plain';
	let label = 'Mail';
	let title = 'Captured from email';
	if (r && r.awaiting) {
		state = 'awaiting';
		label = 'Awaiting reply';
		title = 'From email — the family is waiting on our reply';
	} else if (r && r.lastOutboundBy) {
		state = 'replied';
		label = 'Replied';
		title = `From email — replied by ${r.lastOutboundBy}`;
	}
	return (
		<span className={`wt-mailchip wt-mailchip--${state}`} title={title}>
			<i className="fa-sharp fa-solid fa-envelope" aria-hidden="true" />
			<span className="wt-mailchip__label">{label}</span>
		</span>
	);
}

export function PriorityMark({ priority }) {
	if (priority === 'high') {
		return (
			<span className="wt-pri wt-pri--high" title="High priority">
				<i className="fa-sharp fa-solid fa-flag" />
			</span>
		);
	}
	if (priority === 'low') {
		return (
			<span className="wt-pri wt-pri--low" title="Low priority">
				<i className="fa-sharp fa-solid fa-chevron-down" />
			</span>
		);
	}
	return null;
}

// Auto-triage signal on a queue row: this capture is carrying a classifier
// proposal nobody has looked at. `proposed` says how many fields approving it
// would change; `needsInfo` says the classifier declined to guess. Both open the
// detail — the row deliberately carries no approve affordance.
export function TriageBadge({ task }) {
	// isActionable, not hasProposal: an APPROVED payload is kept on the doc as
	// provenance, and badging it would leave every triaged task wearing a
	// "Proposal" chip it no longer needs.
	if (!isActionable(task)) return null;
	if (isNeedsInfo(task)) {
		return (
			<span className="wt-triagebadge wt-triagebadge--needsinfo" title="Auto-triage needs more information">
				<i className="fa-sharp fa-solid fa-circle-question" />
				Needs info
			</span>
		);
	}
	const n = changeCount(task);
	return (
		<span
			className="wt-triagebadge"
			title={
				n === 0
					? 'Auto-triage proposal — nothing would change'
					: `Auto-triage proposal — ${n} field${n === 1 ? '' : 's'} would change`
			}>
			<i className="fa-sharp fa-solid fa-wand-magic-sparkles" />
			Proposal
			{n > 0 && <span className="wt-triagebadge__count">{n}</span>}
		</span>
	);
}

// Mini progress for a parent's sub-tasks: "1/2" + two small bars.
export function SubtaskProgress({ children }) {
	const { done, total } = childProgress(children);
	if (!total) return null;
	return (
		<span className="wt-subprog" title={`${done} of ${total} sub-tasks done`}>
			<i className="fa-sharp fa-solid fa-list-tree" />
			{done}/{total}
			<span className="wt-subprog__bars">
				{Array.from({ length: total }).map((_, i) => (
					<span
						key={i}
						className={`wt-subprog__bar${i < done ? ' is-done' : ''}`}
					/>
				))}
			</span>
		</span>
	);
}

// Small status pill — shown on board cards in place of the grouped dimension
// (e.g. when grouped by area, the assignee slot can carry status, and vice
// versa) so a card never repeats the column it already lives in.
const STATUS_LABEL = {
	pending: 'Pending',
	in_progress: 'In progress',
	completed: 'Done',
};
// Quiet dot + label (mock v5) — a coloured dot keyed to the status hue, not a
// filled pill. The dot colour resolves to the same --status-* identity tokens as
// the status circle.
export function StatusChip({ status }) {
	return (
		<span className={`wt-statuschip wt-statuschip--${status}`}>
			<span className="wt-statuschip__dot" />
			{STATUS_LABEL[status] || status}
		</span>
	);
}

// "Done · Jun 20" using the real closedAt (falls back to nothing if unset).
export function DoneChip({ closedAt }) {
	if (!closedAt) return null;
	return (
		<span className="wt-done-chip">
			<i className="fa-sharp fa-solid fa-circle-check" />
			Done · {formatShortDate(closedAt)}
		</span>
	);
}

export function ProgressBar({ pct, accentArea }) {
	const style = accentArea ? accentStyle(accentArea) : undefined;
	return (
		<span className="wt-progress" style={style}>
			<span
				className="wt-progress__fill"
				style={{ width: `${Math.max(0, Math.min(100, pct))}%` }}
			/>
		</span>
	);
}
